import * as React from 'react';
import { type LucideIcon } from 'lucide-react';
import { Card, CardContent } from '../primitives/card';
import { Button, buttonVariants } from '../primitives/button';
import { cn } from '../../lib/utils';

export type ResultTone = 'success' | 'info' | 'warning' | 'destructive';

export interface ResultCardProps {
  tone?: ResultTone;
  icon?: LucideIcon;
  title: string;
  description?: React.ReactNode;
  /** The one thing to do next. Errors should always offer one. */
  action?: { label: string; onAction?: () => void; href?: string };
  /** Quieter alternative, plus any support reference. */
  footer?: React.ReactNode;
  testId?: string;
  className?: string;
}

const chipFor: Record<ResultTone, string> = {
  success: 'bg-emerald-50 text-emerald-600',
  info: 'bg-primary/10 text-primary',
  warning: 'bg-amber-50 text-amber-600',
  destructive: 'bg-destructive/10 text-destructive',
};

/**
 * The outcome of something the person just did, or something that went wrong.
 *
 * One shape for the whole family — email verified, reset link sent, invitation
 * expired, invitation already used — because they differ only in wording and
 * icon. Previously each was its own layout, so the error screens drifted into
 * a large warning triangle with a dead end under it.
 *
 * Errors get the same treatment as successes deliberately: an error is still
 * a result, and it should say what happened and offer the way forward rather
 * than just signalling alarm.
 */
export function ResultCard({
  tone = 'info',
  icon: Icon,
  title,
  description,
  action,
  footer,
  testId,
  className,
}: ResultCardProps) {
  return (
    <Card className={cn('w-full max-w-md', className)} data-testid={testId}>
      <CardContent className="p-6 text-center sm:p-8">
        {Icon && (
          <span
            className={cn(
              'mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full',
              chipFor[tone],
            )}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </span>
        )}
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        {description && <p className="mt-1.5 text-sm text-muted-foreground">{description}</p>}

        {action &&
          (action.href ? (
            // A real link, so it can be opened in a new tab and read as a
            // destination — styled to match the button it stands in for.
            <a href={action.href} className={cn(buttonVariants(), 'mt-6 w-full')}>
              {action.label}
            </a>
          ) : (
            <Button className="mt-6 w-full" onClick={action.onAction}>
              {action.label}
            </Button>
          ))}

        {footer && <div className="mt-4 text-xs text-muted-foreground">{footer}</div>}
      </CardContent>
    </Card>
  );
}
